import { Fragment, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import PercentChange from "../PercentChange";
import { CloseIcon, CopyIcon } from "../../icons";
import { ALL_YOUR_ACCOUNTS, ASSET_COUNT } from "../../mockData";

const parseNumericValue = (label) => {
  const match = String(label).match(/[\d,]+\.?\d*/);
  return match ? Number(match[0].replace(/,/g, "")) : 0;
};

const groupAccountsForModal = (accounts) => {
  const groups = [];
  let usdtBatch = [];

  const flushUsdt = () => {
    if (!usdtBatch.length) return;

    groups.push({
      symbol: "USDT",
      isGrouped: usdtBatch.length > 1,
      accounts: usdtBatch,
    });
    usdtBatch = [];
  };

  accounts.forEach((account) => {
    if (account.symbol === "USDT") {
      usdtBatch.push(account);
      return;
    }

    flushUsdt();
    groups.push({
      symbol: account.symbol,
      isGrouped: false,
      accounts: [account],
    });
  });

  flushUsdt();
  return groups;
};

const formatTotalLabel = (total, symbol) =>
  `${total.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${symbol}`;

const formatUsdTotal = (total) =>
  `$${total.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const AccountRow = ({
  account,
  showBorder,
  compact = false,
  nested = false,
}) => (
  <li
    className={`flex items-center justify-between ${
      nested
        ? `min-h-[70px] px-3 py-2.5 ${showBorder ? "border-t border-divider" : ""}`
        : `min-h-[78px] px-4 py-3 sm:px-5 ${showBorder ? "border-b border-divider" : ""}`
    }`}
  >
    <div className="flex min-w-0 items-center gap-3">
      <div
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full ${
          compact ? "size-7" : "size-9"
        }`}
        style={account.iconBg ? { backgroundColor: account.iconBg } : undefined}
      >
        <img
          src={account.logo}
          alt={account.symbol}
          className={`rounded-full object-cover ${compact ? "size-7" : "size-9"}`}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        {compact ? (
          <>
            <p className="whitespace-nowrap text-xs font-normal leading-[16.5px] text-text-secondary">
              Network: {account.network}
            </p>
            <div className="flex items-center gap-2 bg-background-alt px-1 py-0.5">
              <span className="whitespace-nowrap text-xs font-normal leading-[16.5px] text-text-secondary">
                {account.walletAddress}
              </span>
              <button
                type="button"
                className="shrink-0 text-text-secondary custom-transition hover:text-text-primary"
                aria-label={`Copy ${account.symbol} address`}
                onClick={() =>
                  navigator.clipboard?.writeText(account.walletAddress)
                }
              >
                <CopyIcon />
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="whitespace-nowrap text-text-primary">
              <span className="text-base font-medium leading-[19.5px]">
                {account.symbol}
              </span>
              <span className="text-base font-light leading-[19.5px] text-text-secondary">
                {" "}
                |{" "}
              </span>
              <span className="text-xs font-normal leading-[19.5px] text-text-secondary">
                {account.network}
              </span>
            </p>
            <div className="flex items-center gap-2 bg-background-alt px-1 py-0.5">
              <span className="whitespace-nowrap text-xs font-normal leading-[16.5px] text-text-secondary">
                {account.walletAddress}
              </span>
              <button
                type="button"
                className="shrink-0 text-text-secondary custom-transition hover:text-text-primary"
                aria-label={`Copy ${account.symbol} address`}
                onClick={() =>
                  navigator.clipboard?.writeText(account.walletAddress)
                }
              >
                <CopyIcon />
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    <div className="flex shrink-0 flex-col items-end">
      <p className="whitespace-nowrap text-sm font-medium leading-[19.5px] text-text-primary">
        {account.amountLabel}
      </p>
      <p className="whitespace-nowrap text-[11px] font-normal leading-[16.5px] text-text-secondary">
        {account.usdLabel}
      </p>
      <PercentChange value={account.change24h} positive={account.positive} />
    </div>
  </li>
);

const GroupedNetworkRow = ({ account, showBorder }) => (
  <div
    className={`flex items-center justify-between px-5 ${
      showBorder ? "border-b border-divider" : ""
    }`}
  >
    <div className="flex min-w-0 items-center gap-2">
      <div
        className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full"
        style={{ backgroundColor: account.iconBg }}
      >
        <img
          src={account.logo}
          alt={account.symbol}
          className="size-6 rounded-full object-cover"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <p className="whitespace-nowrap text-xs font-medium leading-[19.5px] text-text-primary">
          Network: {account.network}
        </p>
        <div className="flex items-center gap-2 bg-background-alt py-0.5">
          <span className="whitespace-nowrap text-xs font-normal leading-[16.5px] text-text-secondary">
            {account.walletAddress}
          </span>
          <button
            type="button"
            className="shrink-0 text-text-secondary custom-transition hover:text-text-primary"
            aria-label={`Copy ${account.symbol} address`}
            onClick={() =>
              navigator.clipboard?.writeText(account.walletAddress)
            }
          >
            <CopyIcon />
          </button>
        </div>
      </div>
    </div>

    <div className="flex shrink-0 flex-col items-end">
      <p className="whitespace-nowrap text-sm font-medium leading-[19.5px] text-text-primary">
        {account.amountLabel}
      </p>
      <p className="whitespace-nowrap text-[11px] font-normal leading-[16.5px] text-text-secondary">
        {account.usdLabel}
      </p>
      <PercentChange value={account.change24h} positive={account.positive} />
    </div>
  </div>
);

/** Figma 1117:4272 — grouped USDT container */
const GroupedAccountSection = ({ group }) => {
  const primaryAccount = group.accounts[0];
  const totalAmount = group.accounts.reduce(
    (sum, account) => sum + parseNumericValue(account.amountLabel),
    0,
  );
  const totalUsd = group.accounts.reduce(
    (sum, account) => sum + parseNumericValue(account.usdLabel),
    0,
  );

  return (
    <li className="flex flex-col gap-2 rounded-lg border-x border-b border-divider">
      <div className="flex items-center justify-between bg-background-alt px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: primaryAccount.iconBg }}
          >
            <img
              src={primaryAccount.logo}
              alt={group.symbol}
              className="size-9 rounded-full object-cover"
            />
          </div>
          <p className="text-base font-medium leading-[19.5px] text-text-primary">
            {group.symbol}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end text-right">
          <p className="whitespace-nowrap text-sm font-semibold leading-[19.5px] text-text-primary">
            {formatTotalLabel(totalAmount, group.symbol)}
          </p>
          <p className="whitespace-nowrap text-[11px] font-normal leading-[16.5px] text-text-secondary">
            {formatUsdTotal(totalUsd)}
          </p>
        </div>
      </div>

      <div className="flex w-full flex-col gap-6">
        {group.accounts.map((account, index) => (
          <GroupedNetworkRow
            key={account.id}
            account={account}
            showBorder={index < group.accounts.length - 1}
          />
        ))}
      </div>
    </li>
  );
};

const AccountsList = ({ groups }) => (
  <ul className="min-h-0 flex-1  sm:pl-6 pl-4 pr-3 custom-scrollbar mr-2 overflow-y-auto">
    {groups.map((group, index) =>
      group.isGrouped ? (
        <GroupedAccountSection key={group.symbol} group={group} />
      ) : (
        <AccountRow
          key={group.accounts[0].id}
          account={group.accounts[0]}
          showBorder={index < groups.length - 1}
        />
      ),
    )}
  </ul>
);

const YourAccountsModal = ({ isOpen, onClose }) => {
  const accountGroups = useMemo(
    () => groupAccountsForModal(ALL_YOUR_ACCOUNTS),
    [],
  );

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-[3px]" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden p-0 sm:p-5">
          <div className="flex min-h-full items-end justify-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="flex w-full max-w-[552px] flex-col sm:gap-3">
                <div className="flex w-full flex-col items-center sm:hidden">
                  <button
                    type="button"
                    onClick={onClose}
                    className="mb-4 flex size-10 shrink-0 items-center justify-center rounded-full bg-[#9CA3AF] shadow-[0_2px_8px_rgba(0,0,0,0.15)] custom-transition hover:bg-[#8B939E]"
                    aria-label="Close"
                  >
                    <CloseIcon className="text-white" />
                  </button>
                </div>

                <div className="flex max-h-[85dvh] w-full flex-col overflow-hidden rounded-t-[24px] bg-background-surface py-[17px] shadow-[0_4px_24px_rgba(0,0,0,0.16)] sm:max-h-[560px] sm:rounded-[16px]">
                  <div className="flex h-12 shrink-0 items-center justify-between px-4 sm:px-6">
                    <Dialog.Title className="text-lg font-semibold leading-7 text-text-primary">
                      Your Accounts ({ASSET_COUNT})
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={onClose}
                      className="hidden rounded-full p-0 custom-transition hover:bg-option-hover sm:block"
                      aria-label="Close"
                    >
                      <CloseIcon />
                    </button>
                  </div>

                  <AccountsList groups={accountGroups} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default YourAccountsModal;
